import { X } from "lucide-react";
import { useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import styles from "../../../styles/FormStepRenderer.module.css";
import { convertHeicFiles } from "../../../utils/heicConverter";
import CardOption from "./OptionCard";
import PhoneNumberInput from "./PhoneNumberInput";

// Email validation component
const EmailInputWithValidation = ({ inputRef, step, value, onChange, dynamicLabel, hideLabel, theme }) => {
    const [emailError, setEmailError] = useState(null);

    const validateEmail = (email) => {
        if (!email || email.trim() === "") return null; // Empty is valid (unless required)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    const handleChange = (newValue) => {
        onChange(newValue);
        if (step.type === "email") {
            const isValid = validateEmail(newValue);
            if (newValue && newValue.trim() !== "") {
                setEmailError(isValid ? null : "Please enter a valid email address");
            } else {
                setEmailError(null);
            }
        }
    };

    const handleBlur = (e) => {
        if (step.type === "email") {
            const isValid = validateEmail(e.target.value);
            if (e.target.value && e.target.value.trim() !== "") {
                setEmailError(isValid ? null : "Please enter a valid email address");
            } else {
                setEmailError(null);
            }
        }
        e.target.style.borderColor = emailError 
            ? "#dc3545" 
            : (theme?.inputBorderColor || "#e5e7eb");
        e.target.style.boxShadow = `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`;
    };

    return (
        <>
            <Form.Control
                ref={inputRef}
                type={step.type}
                pattern={(() => {
                    const pattern = typeof step.validation?.pattern === "string"
                        ? step.validation.pattern
                        : step.validation?.pattern?.source;
                    
                    // Fix common regex pattern issues
                    if (pattern) {
                        // Fix the specific issue: ^+?[0-9s-] -> ^[+]?[0-9\s-]
                        const fixedPattern = pattern
                            .replace(/^\^\+/, '^[+]') // Fix ^+ -> ^[+]
                            .replace(/\[0-9s-\]/g, '[0-9\\s-]'); // Fix [0-9s-] -> [0-9\s-]
                        
                        // Validate the regex pattern
                        try {
                            new RegExp(fixedPattern);
                            return fixedPattern;
                        } catch (error) {
                            console.warn('Invalid regex pattern, using fallback:', pattern);
                            return undefined;
                        }
                    }
                    return pattern;
                })()}
                required={step.validation?.required}
                title={step.validation?.message}
                value={value || ""}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") handleChange(e.target.value);
                }}
                className={styles.textInput}
                placeholder={hideLabel ? dynamicLabel : ""}
                style={{
                    borderRadius: theme?.inputBorderRadius,
                    borderColor: emailError 
                        ? "#dc3545" 
                        : (theme?.inputBorderColor || "#e5e7eb"),
                    backgroundColor: theme?.inputBackground,
                    color: theme?.inputTextColor,
                    boxShadow: `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`,
                }}
                onFocus={(e) => {
                    e.target.style.borderColor = theme?.inputFocusBorderColor || "#348C9E";
                    e.target.style.boxShadow = `0 0 0 4px rgba(52, 140, 158, 0.1), 0 4px 12px rgba(52, 140, 158, 0.15)`;
                }}
                onBlur={handleBlur}
                onMouseEnter={(e) => {
                    if (document.activeElement !== e.target) {
                        e.target.style.borderColor = emailError
                            ? "#dc3545"
                            : (theme?.inputHoverBorderColor || "#d1d5db");
                    }
                }}
                onMouseLeave={(e) => {
                    if (document.activeElement !== e.target) {
                        e.target.style.borderColor = emailError
                            ? "#dc3545"
                            : (theme?.inputBorderColor || "#e5e7eb");
                    }
                }}
            />
            {emailError && (
                <small className={styles.validationMessage} style={{ color: "#dc3545" }}>
                    {emailError}
                </small>
            )}
        </>
    );
};

const FormStepRenderer = ({
    step,
    value,
    onChange,
    formData,
    inventoryItems = [],
    inventoryLoading = false,
    hideLabel = false,
    inputRef,
    theme,
    formStyle
}) => {

    const [selectedTier, setSelectedTier] = useState("");
    // Track primary image index for each image field (keyed by step label)
    const [primaryIndices, setPrimaryIndices] = useState({});
    // Loader shown while converting/adding selected images
    const [imageAdding, setImageAdding] = useState(false);
    
    // Refs for immediate updates without re-render (for performance on mobile)
    const primaryIndexRefs = useRef({});
    const imageUrlCache = useRef({});
    

    const getFilteredOptions = () => {
        const allOptions = step.options || [];
        
        // If this is a "Category" step, generate options from inventory categories
        if (step.label === "Category") {
            if (inventoryItems && Array.isArray(inventoryItems) && inventoryItems.length > 0) {
                const categories = inventoryItems
                    .map(item => item?.category)
                    .filter(category => category && category.trim()) // Remove empty categories
                    .filter((category, index, array) => array.indexOf(category) === index) // Remove duplicates
                    .sort(); // Sort alphabetically
                
                if (categories.length > 0) {
                    return categories.map(category => ({
                        label: category,
                        parent: null
                    }));
                }
            }
            // If no inventory items or no categories found, return empty array
            return [];
        }
        
        // If this is a "Product Name" step and depends on "Category", generate options from inventory
        if (step.label === "Product Name" && step.dependsOn === "Category" && formData) {
            const selectedCategory = formData[step.dependsOn];
            
            if (selectedCategory && inventoryItems.length > 0) {
                // Normalize category for comparison (trim and lowercase)
                const normalizedSelectedCategory = String(selectedCategory).trim().toLowerCase();
                
                // Get unique product names for the selected category
                const categoryProducts = inventoryItems
                    .filter(item => {
                        const itemCategory = item.category ? String(item.category).trim().toLowerCase() : "";
                        return itemCategory === normalizedSelectedCategory;
                    })
                    .map(item => item.productName)
                    .filter((name, index, array) => array.indexOf(name) === index) // Remove duplicates
                    .sort(); // Sort alphabetically
                
                // Find the matching inventory items to preserve image URLs
                const productsWithImages = categoryProducts.map(productName => {
                    const matchingItem = inventoryItems.find(item => 
                        item.productName?.toLowerCase() === productName.toLowerCase()
                    );
                    return {
                        label: productName,
                        parent: selectedCategory,
                        imageUrl: matchingItem?.imageUrl || null
                    };
                });
                
                return productsWithImages;
            }
            return [];
        }
        
        // If this is a "Primary Variant" step and depends on "Product Name", generate options from inventory
        if (step.label === "Primary Variant" && step.dependsOn === "Product Name" && formData) {
            const selectedProduct = formData[step.dependsOn];
            
            if (selectedProduct && inventoryItems.length > 0) {
                // Normalize product name for comparison (trim and lowercase)
                const normalizedSelectedProduct = String(selectedProduct).trim().toLowerCase();
                
                // Find the matching inventory item
                const matchedItem = inventoryItems.find(item => {
                    const itemProductName = item.productName ? String(item.productName).trim().toLowerCase() : "";
                    return itemProductName === normalizedSelectedProduct;
                });
                
                if (matchedItem && matchedItem.items) {
                    
                    // Get unique primary variants for the selected product
                    const primaryVariants = matchedItem.items
                        .filter(variant => variant.primary && variant.primary.trim())
                        .map(variant => variant.primary)
                        .filter((variant, index, array) => array.indexOf(variant) === index) // Remove duplicates
                        .sort(); // Sort alphabetically
                    
                    // Find the matching variants to preserve image URLs
                    const variantsWithImages = primaryVariants.map(variant => {
                        const matchingVariant = matchedItem.items.find(item => 
                            item.primary?.toLowerCase() === variant.toLowerCase()
                        );
                        return {
                            label: variant,
                            parent: selectedProduct,
                            imageUrl: matchingVariant?.imageUrl || matchedItem?.imageUrl || null
                        };
                    });
                    
                    return variantsWithImages;
                }
            }
            return [];
        }
        
        // Default filtering logic for other cases
        // If step has dependsOn, filter by parent value
        if (step.dependsOn) {
            if (!formData) return [];
            const parentValue = formData[step.dependsOn];
            if (!parentValue) return [];
            return allOptions.filter(opt =>
                typeof opt === "object" ? opt.parent === parentValue : false
            );
        }
        
        // If no dependsOn, return all options
        return allOptions;
    };

    const filteredOptions = getFilteredOptions();

    const dynamicLabel =
        step.dynamicLabelFrom && step.dependsOn && formData?.[step.dependsOn]
            ? step.dynamicLabelFrom[formData[step.dependsOn]] || step.label
            : step.label;

    const selectedProduct = formData["Product Name"];
    const selectedVariant = formData["Primary Variant"];
    const matchedItem = inventoryItems.find(item =>
        item.productName?.toLowerCase() === selectedProduct?.toLowerCase()
    );
    const variantData = matchedItem?.items?.find(i =>
        i.primary?.toLowerCase() === selectedVariant?.toLowerCase()
    );
    const selectedPriceEntry = variantData?.prices?.find(p => p.name === selectedTier);

    const isSinglePage = formStyle === "singlePage";
    
    return (
        <div 
            className={styles.stepContainer}
            style={isSinglePage && step.type === "image" ? {
                textAlign: "left",
                margin: "0",
                maxWidth: "100%",
                width: "100%"
            } : {}}
        >
            <Form.Group 
                className={styles.formGroup}
                style={isSinglePage && step.type === "image" ? {
                    textAlign: "left",
                    margin: "0",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%"
                } : {}}
            >
                {/* Select with cards */}
                {step.type === "select" && (
                    <div className={styles.optionGrid}>
                        {filteredOptions.length === 0 ? (
                            <div style={{ padding: "1rem", color: "#6c757d", textAlign: "center" }}>
                                {inventoryLoading && (step.label === "Category" || step.label === "Product Name")
                                    ? "Loading options..."
                                    : step.label === "Primary Variant" && selectedProduct && matchedItem && !Array.isArray(matchedItem.items)
                                        ? "Loading options..."
                                        : step.label === "Category"
                                            ? "No categories found in inventory"
                                            : "No options available"}
                            </div>
                        ) : (
                            filteredOptions.map((opt, i) => {
                                const optionValue = typeof opt === "object" ? opt.label : opt;
                                const imageUrl = typeof opt === "object" ? opt.imageUrl : null;
                                
                                // Normalize comparison to handle case sensitivity and whitespace
                                const normalizedValue = value ? String(value).trim().toLowerCase() : "";
                                const normalizedOptionValue = optionValue ? String(optionValue).trim().toLowerCase() : "";
                                const isSelected = normalizedValue === normalizedOptionValue && normalizedValue !== "";

                                return (
                                    <CardOption
                                        key={i}
                                        label={optionValue}
                                        selected={isSelected}
                                        onClick={() => onChange(optionValue)}
                                        imageUrl={imageUrl}
                                        customStyles={{
                                            card: {
                                                background: theme?.cardBackgroundColor || "#ffffff",
                                                backgroundColor: theme?.cardBackgroundColor || "#ffffff",
                                                borderColor: theme?.cardBorderColor || "#e5e7eb",
                                                borderRadius: theme?.cardBorderRadius || "16px",
                                                boxShadow: `0 2px 8px rgba(0, 0, 0, ${theme?.cardShadowIntensity || 0.08}), 0 0 0 1px rgba(0, 0, 0, 0.02)`,
                                            },
                                        cardImage: {
                                            backgroundColor: "#ffffff",
                                            borderRadius: theme?.cardBorderRadius || "16px",
                                        },
                                            cardLabel: {
                                                color: theme?.cardTextColor || "#1f2937",
                                            },
                                        }}
                                        theme={theme}
                                    />
                                );
                            })
                        )}
                    </div>
                )}

                {/* Variant tier selector */}
                {step.type === "variant" && selectedProduct && selectedVariant && variantData && (
                    <>
                        <Form.Select
                            value={selectedTier}
                            onChange={(e) => setSelectedTier(e.target.value)}
                            className={styles.select}
                            style={{
                                borderRadius: theme?.inputBorderRadius,
                                borderColor: theme?.inputBorderColor,
                                backgroundColor: theme?.inputBackground,
                                color: theme?.inputTextColor,
                                boxShadow: `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`,
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = theme?.inputFocusBorderColor || "#348C9E";
                                e.target.style.boxShadow = `0 0 0 4px rgba(52, 140, 158, 0.1), 0 4px 12px rgba(52, 140, 158, 0.15)`;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = theme?.inputBorderColor || "#e5e7eb";
                                e.target.style.boxShadow = `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`;
                            }}
                            onMouseEnter={(e) => {
                                if (document.activeElement !== e.target) {
                                    e.target.style.borderColor = theme?.inputHoverBorderColor || "#d1d5db";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (document.activeElement !== e.target) {
                                    e.target.style.borderColor = theme?.inputBorderColor || "#e5e7eb";
                                }
                            }}
                        >
                            <option value="">Select price tier...</option>
                            {variantData.prices.map((p, i) => (
                                <option key={i} value={p.name}>{p.name}</option>
                            ))}
                        </Form.Select>

                        {selectedTier && selectedPriceEntry && (
                            <div className={styles.variantDetails}>
                                <strong>{selectedPriceEntry.name ?? "Unnamed Tier"}</strong><br />
                                Price: {selectedPriceEntry.price ?? "N/A"} kr<br />
                                Formula: {selectedPriceEntry.formula ?? "N/A"}<br />
                                ID: {selectedPriceEntry.id ?? "N/A"}
                            </div>
                        )}
                    </>
                )}

                {/* Tel input with country code selector */}
                {step.type === "tel" && (
                    <PhoneNumberInput
                        value={value || ""}
                        onChange={onChange}
                        validation={step.validation}
                        inputRef={inputRef}
                        theme={theme}
                        placeholder={hideLabel ? dynamicLabel : ""}
                    />
                )}

                {/* Text and Email input with validation */}
                {["text", "email"].includes(step.type) && (
                    <>
                        <EmailInputWithValidation
                            inputRef={inputRef}
                            step={step}
                            value={value || ""}
                            onChange={onChange}
                            dynamicLabel={dynamicLabel}
                            hideLabel={hideLabel}
                            theme={theme}
                        />
                    </>
                )}

                {/* Optional validation message - only show for non-tel fields (tel fields handle their own messages) */}
                {step.validation?.message && step.type !== "tel" && step.type !== "email" && (
                    <small className={styles.validationMessage}>
                        {step.validation.message}
                    </small>
                )}

                {/* File uploader (document form - any file type) */}
                {step.type === "file" && (() => {
                    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
                    const MAX_FILES = 20;
                    const formatFileSize = (bytes) => {
                        if (bytes === 0) return "0 B";
                        const k = 1024;
                        const sizes = ["B", "KB", "MB"];
                        const i = Math.min(2, Math.floor(Math.log(bytes) / Math.log(k)));
                        return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
                    };
                    const filesArray = value ? (Array.isArray(value) ? value : [value]) : [];
                    const handleRemoveFile = (indexToRemove) => {
                        const newFiles = filesArray.filter((_, i) => i !== indexToRemove);
                        onChange(newFiles.length === 0 ? null : (newFiles.length === 1 ? newFiles[0] : newFiles));
                    };
                    const handleAddFiles = (newFiles) => {
                        const existing = filesArray.filter(f => f instanceof File);
                        const valid = newFiles.filter(f => f instanceof File && f.size <= MAX_FILE_SIZE);
                        const rejected = newFiles.filter(f => f instanceof File && f.size > MAX_FILE_SIZE);
                        if (rejected.length > 0) {
                            toast.warning(`${rejected.length} file(s) were too large (max 10MB) and were not added.`);
                        }
                        const combined = [...existing, ...valid];
                        if (combined.length > MAX_FILES) {
                            const kept = combined.slice(0, MAX_FILES);
                            toast.warning(`Maximum ${MAX_FILES} files. Some were not added.`);
                            onChange(kept.length === 1 ? kept[0] : kept);
                        } else {
                            onChange(combined.length === 1 ? combined[0] : combined);
                        }
                    };
                    const fileCount = filesArray.filter(f => f instanceof File).length;
                    const isComplete = fileCount > 0;
                    return (
                        <div className={styles.imageUploadContainer}>
                            {!hideLabel && (
                                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.5rem" }}>
                                    <label style={{ marginBottom: 0 }}>{step.label}</label>
                                    {isComplete ? (
                                        <span style={{
                                            fontSize: "0.8rem",
                                            fontWeight: "600",
                                            color: "#15803d",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "0.25rem",
                                        }}>
                                            ✓ Complete — {fileCount} file{fileCount !== 1 ? "s" : ""} added
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                                            Add at least one file
                                        </span>
                                    )}
                                </div>
                            )}
                            <Form.Control
                                type="file"
                                accept="*/*"
                                multiple
                                onChange={(e) => {
                                    const newFiles = Array.from(e.target.files || []).filter(f => f instanceof File);
                                    if (newFiles.length > 0) handleAddFiles(newFiles);
                                    e.target.value = "";
                                }}
                                className={styles.fileInput}
                            />
                            <p className="text-muted small mt-1 mb-0" style={{ fontSize: "0.8rem" }}>
                                Max 10MB per file. You can add up to {MAX_FILES} files.
                            </p>
                            {filesArray.length > 0 && (
                                <ul style={{ marginTop: "0.5rem", paddingLeft: "1.25rem" }}>
                                    {filesArray.map((file, index) => file instanceof File && (
                                        <li key={index} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                                            <span style={{ flex: 1, fontSize: "0.875rem" }}>
                                                {file.name} <span style={{ color: "#6b7280" }}>({formatFileSize(file.size)})</span>
                                            </span>
                                            <button type="button" onClick={() => handleRemoveFile(index)} aria-label="Remove file" className={styles.removeImageButton}>
                                                <X size={16} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })()}

                {/* Image uploader */}
                {step.type === "image" && (() => {
                    // Convert single file to array for consistent handling
                    const filesArray = value ? (Array.isArray(value) ? value : [value]) : [];
                    const fieldKey = step.label || "image";
                    
                    // Initialize ref for this field if not exists
                    if (!primaryIndexRefs.current[fieldKey]) {
                        primaryIndexRefs.current[fieldKey] = primaryIndices[fieldKey] ?? 0;
                    }
                    
                    // Sync ref with state when state changes (from external sources)
                    const currentPrimaryIndex = primaryIndices[fieldKey] ?? 0;
                    const statePrimaryIndex = filesArray.length > 0 
                        ? Math.min(currentPrimaryIndex, filesArray.length - 1)
                        : 0;
                    
                    if (statePrimaryIndex !== primaryIndexRefs.current[fieldKey]) {
                        primaryIndexRefs.current[fieldKey] = statePrimaryIndex;
                    }
                    
                    // Use ref value directly for immediate visual feedback (faster on mobile)
                    // This avoids waiting for state updates
                    const primaryIndex = filesArray.length > 0 
                        ? Math.min(primaryIndexRefs.current[fieldKey], filesArray.length - 1)
                        : 0;

                    // Cache object URLs to prevent recreation on every render
                    // This significantly improves performance on mobile
                    const fileKey = filesArray.map(f => 
                        f instanceof File ? `${f.name}-${f.size}-${f.lastModified}` : ''
                    ).join('|');
                    
                    if (!imageUrlCache.current[fieldKey] || 
                        imageUrlCache.current[fieldKey].key !== fileKey) {
                        // Cleanup old URLs to prevent memory leaks
                        if (imageUrlCache.current[fieldKey]?.urls) {
                            imageUrlCache.current[fieldKey].urls.forEach(url => {
                                if (url && typeof url === 'string' && url.startsWith('blob:')) {
                                    URL.revokeObjectURL(url);
                                }
                            });
                        }
                        // Create new URLs only when files change
                        const urls = filesArray.map(file => {
                            if (file instanceof File) {
                                return URL.createObjectURL(file);
                            }
                            return null;
                        });
                        imageUrlCache.current[fieldKey] = { key: fileKey, urls };
                    }
                    const imageUrls = imageUrlCache.current[fieldKey]?.urls || [];

                    const handleRemoveImage = (indexToRemove) => {
                        const newFiles = filesArray.filter((_, index) => index !== indexToRemove);
                        if (newFiles.length === 0) {
                            onChange(null);
                            primaryIndexRefs.current[fieldKey] = 0;
                            setPrimaryIndices(prev => ({ ...prev, [fieldKey]: 0 }));
                        } else if (newFiles.length === 1) {
                            onChange(newFiles[0]);
                            primaryIndexRefs.current[fieldKey] = 0;
                            setPrimaryIndices(prev => ({ ...prev, [fieldKey]: 0 }));
                        } else {
                            onChange(newFiles);
                            // Adjust primary index if needed
                            let newPrimaryIndex = primaryIndexRefs.current[fieldKey];
                            if (primaryIndexRefs.current[fieldKey] >= newFiles.length) {
                                newPrimaryIndex = Math.max(0, newFiles.length - 1);
                            } else if (indexToRemove < primaryIndexRefs.current[fieldKey]) {
                                newPrimaryIndex = primaryIndexRefs.current[fieldKey] - 1;
                            }
                            primaryIndexRefs.current[fieldKey] = newPrimaryIndex;
                            setPrimaryIndices(prev => ({ ...prev, [fieldKey]: newPrimaryIndex }));
                        }
                    };

                    // Optimized: Update ref immediately, then update state synchronously
                    // The ref value is used in render for instant visual feedback
                    const handleSetPrimary = (index) => {
                        const currentRef = primaryIndexRefs.current[fieldKey];
                        if (currentRef === index) return; // No change needed
                        
                        // Update ref immediately for instant visual feedback
                        primaryIndexRefs.current[fieldKey] = index;
                        
                        // Update state synchronously - React will batch this efficiently
                        setPrimaryIndices(prev => ({ ...prev, [fieldKey]: index }));
                    };

                    const handleAddFiles = (newFiles) => {
                        const existingFiles = filesArray.filter(f => f instanceof File);
                        const allFiles = [...existingFiles, ...newFiles];
                        onChange(allFiles.length === 1 ? allFiles[0] : allFiles);
                        // Set first new file as primary if no files existed before
                        if (existingFiles.length === 0 && newFiles.length > 0) {
                            primaryIndexRefs.current[fieldKey] = 0;
                            setPrimaryIndices(prev => ({ ...prev, [fieldKey]: 0 }));
                        }
                    };

                    const imageFileCount = filesArray.filter(f => f instanceof File).length;
                    const imageStepComplete = imageFileCount > 0;
                    const imageContainerStyle = isSinglePage ? {
                        width: "100%",
                        textAlign: "left",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "1rem",
                        marginTop: "0",
                        marginLeft: "0",
                        marginRight: "0",
                        marginBottom: "0"
                    } : {};
                    return (
                        <div
                            className={styles.imageUploadContainer}
                            style={imageContainerStyle}
                        >
                            {!hideLabel && (
                                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", width: isSinglePage ? "100%" : undefined }}>
                                    <label style={isSinglePage ? { textAlign: "left", margin: "0", display: "block" } : {}}>{step.label}</label>
                                    {imageStepComplete ? (
                                        <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#15803d", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                                            ✓ Complete — {imageFileCount} file{imageFileCount !== 1 ? "s" : ""} added
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Add at least one file</span>
                                    )}
                                </div>
                            )}
                            {imageAdding && (
                                <div className={styles.imageAddingLoader}>
                                    <div className={styles.imageAddingSpinner} />
                                    <span>Processing and adding images...</span>
                                </div>
                            )}
                            <Form.Control
                                type="file"
                                accept="image/*,.heic,.heif,.jfif"
                                multiple
                                onChange={async (e) => {
                                    const newFiles = Array.from(e.target.files || []).filter(f => f instanceof File);
                                    if (newFiles.length > 0) {
                                        setImageAdding(true);
                                        try {
                                            const converted = await convertHeicFiles(newFiles);
                                            handleAddFiles(converted);
                                        } catch (err) {
                                            console.error("Image conversion failed:", err);
                                            toast.error("Couldn't convert image. Please upload JPG/PNG instead.");
                                        } finally {
                                            setImageAdding(false);
                                        }
                                    }
                                    // Reset input to allow selecting same files again
                                    e.target.value = '';
                                }}
                                disabled={imageAdding}
                                className={styles.fileInput}
                                style={isSinglePage ? {
                                    width: "100%",
                                    maxWidth: "100%",
                                    textAlign: "left",
                                    margin: "0",
                                    marginBottom: "0.75rem",
                                    marginLeft: "0",
                                    marginRight: "0"
                                } : {}}
                            />
                            
                            {/* Image Gallery: Primary + Thumbnails */}
                            {filesArray.length > 0 && (
                                <div className={styles.imageGallery}>
                                    {/* Primary Image Display */}
                                    {filesArray[primaryIndex] && 
                                     filesArray[primaryIndex] instanceof File && 
                                     imageUrls[primaryIndex] && (
                                        <div className={styles.primaryImageContainer}>
                                            <div className={styles.primaryImageWrapper}>
                                                <img
                                                    src={imageUrls[primaryIndex]}
                                                    alt="Primary preview"
                                                    className={styles.primaryImage}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles.removeImageButton}
                                                    onClick={() => handleRemoveImage(primaryIndex)}
                                                    aria-label="Remove image"
                                                >
                                                    <X size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Thumbnail Grid */}
                                    {filesArray.length > 1 && (
                                        <div className={styles.thumbnailGrid}>
                                            {filesArray.map((file, index) => (
                                                file instanceof File && imageUrls[index] && (
                                                    <div
                                                        key={index}
                                                        className={`${styles.thumbnailContainer} ${index === primaryIndex ? styles.thumbnailActive : ''}`}
                                                        onClick={() => handleSetPrimary(index)}
                                                    >
                                                        <img
                                                            src={imageUrls[index]}
                                                            alt={`Thumbnail ${index + 1}`}
                                                            className={styles.thumbnailImage}
                                                        />
                                                        <button
                                                            type="button"
                                                            className={styles.removeThumbnailButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveImage(index);
                                                            }}
                                                            aria-label="Remove image"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                        {index === primaryIndex && (
                                                            <div className={styles.primaryBadge}>Primary</div>
                                                        )}
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}


            </Form.Group>
        </div>
    );
};

export default FormStepRenderer;
